// Candidature côté bailleur : en-tête du foyer et statut, onglets Résumé et Messages, puis onglets des autres modules
// (pièces, contrôles, décision…).
import { useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { post, put } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { extensions } from "@core/modules";
import { useOrg, useRestricted } from "@core/org";
import { Button, Card, ErrorBox, Input, KeyValues, Loading, Notice, PageHeader, Select, Tabs, Textarea, fieldError, useToast } from "@core/ui";
import type { TemplatesData } from "./campaignTabs";
import { AppStatusBadge, CheckCounts, rentLine } from "./shared";

export type PersonView = {
  id: string;
  role: string;
  role_label: string;
  display_name: string;
  first_name?: string;
  last_name?: string;
  guarantor_of?: string | null;
  guarantor_of_name?: string | null;
  guarantee_kind?: string;
  guarantee_kind_label?: string;
  situation?: string;
  situation_label?: string;
  account_linked?: boolean;
  confirmed_at?: string | null;
  invitation_sent_at?: string | null;
  invitation_expires_at?: string | null;
  complete?: boolean;
  email?: string | null;
  phone?: string | null;
  employer?: { name?: string; siren?: string };
  employer_name?: string;
  declared_monthly_income_cents?: number | null;
  problems?: string[];
};

type Household = {
  tenants: number;
  guarantors: number;
  tenants_declared_income_cents: number;
  tenants_income_missing: number;
  guarantors_declared_income_cents: number;
  guarantors_income_missing: number;
  complete: boolean;
  problems: string[];
};

type Detail = {
  application: { id: string; status: string; status_label: string; household_label: string; message: string; source: string; created_at: string; submitted_at: string | null; updated_at: string };
  campaign: { id: string; title: string; status: string; status_label: string; rent_cents: number; charges_cents: number; property_label: string; property_id: string };
  household_visible: boolean;
  persons: PersonView[];
  household: Household | null;
  follow: { stage: string; stage_label: string; assignee_user_id: string | null; next_action: string; next_action_on: string | null };
  events: { id: string; kind: string; to_label: string | null; from_label: string | null; author: string; note: string; details: any; created_at: string }[];
  visits: { id: string; status: string; attendance: string | null; cancelled_by: string | null; label: string; location: string; past: boolean }[];
  checks: { total: number; families: { family: string; label: string; counts: Record<string, number> }[] };
  members: { user_id: string; full_name: string }[];
  stages: { value: string; label: string }[];
};

export function ApplicationPage() {
  const { applicationId = "" } = useParams();
  const org = useOrg();
  const restricted = useRestricted();
  const [params, setParams] = useSearchParams();
  const q = useGet<Detail>(org.api(`/applications/${applicationId}`));
  const tab = params.get("onglet") ?? "resume";
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error ?? new Error("Candidature introuvable.")} />;
  const d = q.data;
  const ext = extensions("applicationTabs", { data: d.application, restricted });
  const current = ext.find((e) => `ext-${e.id}` === tab);
  const items = [{ value: "resume", label: "Résumé" }, { value: "messages", label: "Messages" }, ...ext.map((e) => ({ value: `ext-${e.id}`, label: e.label }))];
  return (
    <div className="page">
      <PageHeader
        eyebrow={`Candidature · ${d.campaign.property_label}`}
        title={d.application.household_label || "Candidature"}
        intro={
          <span className="row">
            <AppStatusBadge status={d.application.status} label={d.application.status_label} />
            <span className="small">
              {d.application.submitted_at ? `Déposée le ${dateTime(d.application.submitted_at)}` : `Commencée le ${dateTime(d.application.created_at)}, pas encore déposée`}
            </span>
            <Link className="small" to={org.to(`campagnes/${d.campaign.id}?onglet=candidatures`)}>
              Campagne « {d.campaign.title} »
            </Link>
          </span>
        }
      />
      <Tabs value={current || tab === "messages" ? tab : "resume"} onChange={(v) => setParams({ onglet: v }, { replace: true })} items={items} />
      {tab === "messages" ? <LandlordMessages applicationId={d.application.id} /> : current ? <current.Component orgId={org.orgId} applicationId={d.application.id} /> : <Summary d={d} />}
    </div>
  );
}

function PersonCard({ p }: { p: PersonView }) {
  const organism = p.role === "garant" && p.guarantee_kind !== "personne";
  return (
    <div className="location-person">
      <div className="row-between">
        <div>
          <strong>{p.display_name || "—"}</strong>
          <span className="block muted small">
            {p.role_label}
            {p.role === "garant" ? (p.guarantor_of_name ? ` de ${p.guarantor_of_name}` : " du foyer") : ""}
            {organism ? ` · ${p.guarantee_kind_label}` : ""}
          </span>
        </div>
        {!organism ? (
          <span className="small">
            {p.account_linked ? "Compte rattaché" : p.invitation_sent_at ? `Invitation envoyée le ${date(p.invitation_sent_at)}, non acceptée` : "Compte non rattaché"}
          </span>
        ) : null}
      </div>
      {organism ? (
        <p className="small muted">{p.guarantee_kind === "visale" ? "Visa Visale déclaré. Un visa n’est pas une garantie activée : l’activation se fait après la signature du bail." : "Garantie d’un organisme déclarée ; son attestation se vérifie avec les pièces."}</p>
      ) : (
        <KeyValues
          items={[
            ["Situation", p.situation_label ?? "—"],
            ["Employeur ou établissement", p.employer_name || "—"],
            ["Revenus mensuels déclarés", p.declared_monthly_income_cents === null || p.declared_monthly_income_cents === undefined ? "Non renseignés" : <span className="num">{money(p.declared_monthly_income_cents)}</span>],
            ["Contact", [p.email, p.phone].filter(Boolean).join(" · ") || "—"],
          ]}
        />
      )}
      {p.role === "garant" && !organism && !p.confirmed_at ? (
        <p className="small">
          <strong>Engagement non confirmé.</strong> Ces informations ont été saisies par le candidat ; le garant n’a pas encore rattaché son compte.
        </p>
      ) : null}
      {p.problems?.length ? <p className="small muted">À compléter : {p.problems.join(" ")}</p> : null}
    </div>
  );
}

function eventText(e: Detail["events"][number]): string {
  switch (e.kind) {
    case "creee":
      return "Candidature commencée";
    case "statut":
      return `Statut : ${e.from_label ? `${e.from_label} → ` : ""}${e.to_label ?? ""}`;
    case "personne_ajoutee":
      return "Personne ajoutée au dossier";
    case "personne_retiree":
      return `Personne retirée du dossier${e.details?.nom ? ` (${e.details.nom})` : ""}`;
    case "personne_modifiee":
      return "Informations d’une personne modifiées";
    case "invitation_envoyee":
      return "Invitation envoyée à une personne du dossier";
    case "invitation_acceptee":
      return "Compte rattaché par une personne du dossier";
    case "suivi":
      return `Suivi : ${e.details?.etape_libelle ?? ""}${e.details?.responsable_modifie ? " (responsable modifié)" : ""}`;
    case "visite_reservee":
      return "Visite réservée";
    case "visite_annulee":
      return `Visite annulée par ${e.details?.par === "bailleur" ? "le bailleur" : "le candidat"}`;
    case "visite_presence":
      return `Présence à la visite : ${e.details?.presence === "venu" ? "venu" : e.details?.presence === "absent" ? "absent" : "non renseignée"}`;
    default:
      return e.kind;
  }
}

function Summary({ d }: { d: Detail }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [follow, setFollow] = useState({ stage: d.follow.stage, assignee_user_id: d.follow.assignee_user_id ?? "", next_action: d.follow.next_action, next_action_on: d.follow.next_action_on ?? "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const tenants = d.persons.filter((p) => p.role !== "garant");
  const guarantors = d.persons.filter((p) => p.role === "garant");

  async function saveFollow(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await put(org.api(`/applications/${d.application.id}/follow`), { ...follow, assignee_user_id: follow.assignee_user_id || null, next_action_on: follow.next_action_on || null });
      toast("Suivi enregistré.");
      invalidate(org.api(`/applications/${d.application.id}`), org.api(`/campaigns/${d.campaign.id}/applications`));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid-2 location-top">
      <div className="stack">
        {!d.household_visible ? (
          <Notice title="Dossier en cours de constitution">
            Le candidat n’a pas encore déposé son dossier. Les informations du foyer seront visibles après le dépôt ; les visites et les messages restent possibles.
          </Notice>
        ) : null}
        <Card title="Foyer">
          {d.household_visible
            ? tenants.map((p) => <PersonCard key={p.id} p={p} />)
            : tenants.map((p) => (
                <p key={p.id}>
                  <strong>{p.display_name}</strong> <span className="muted small">· {p.role_label.toLowerCase()}</span>
                </p>
              ))}
          {d.household ? (
            <p className="small">
              Revenus mensuels déclarés du foyer : <strong className="num">{money(d.household.tenants_declared_income_cents)}</strong>
              {d.household.tenants_income_missing ? ` (${d.household.tenants_income_missing} revenu(s) non renseigné(s))` : ""}. Chaque personne déclare ses propres revenus ; ils ne sont pas vérifiés à ce stade.
            </p>
          ) : null}
          {d.application.message ? (
            <div>
              <p className="stat-label">Message du candidat</p>
              <pre className="message-body">{d.application.message}</pre>
            </div>
          ) : null}
        </Card>
        {d.household_visible ? (
          <Card title="Garanties">
            {guarantors.length ? guarantors.map((p) => <PersonCard key={p.id} p={p} />) : <p className="muted">Aucune garantie déclarée.</p>}
            {d.household?.guarantors_declared_income_cents ? (
              <p className="small">
                Revenus déclarés des garants : <span className="num">{money(d.household.guarantors_declared_income_cents)}</span>, comptés à part de ceux du foyer.
              </p>
            ) : null}
          </Card>
        ) : null}
        <Card title="Constats de contrôle">
          {d.checks.families.length ? (
            <ul className="list-plain location-families">
              {d.checks.families.map((f) => (
                <li key={f.family}>
                  <span>{f.label}</span>
                  <CheckCounts counts={f.counts} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucun contrôle effectué pour l’instant. Un contrôle absent n’est pas un résultat favorable.</p>
          )}
          <p className="small muted">Le détail de chaque constat, avec sa source, se trouve dans les onglets des pièces et des contrôles. Vous décidez.</p>
        </Card>
      </div>
      <div className="stack">
        <Card title="Suivi">
          {org.can("operate") ? (
            <form className="stack" onSubmit={saveFollow}>
              <ErrorBox error={error} />
              <Select label="Étape" value={follow.stage} options={d.stages} error={fieldError(error, "stage")} onChange={(e) => setFollow({ ...follow, stage: e.target.value })} />
              <Select
                label="Suivi par"
                value={follow.assignee_user_id}
                error={fieldError(error, "assignee_user_id")}
                options={[{ value: "", label: "Personne" }, ...d.members.map((m) => ({ value: m.user_id, label: m.full_name }))]}
                onChange={(e) => setFollow({ ...follow, assignee_user_id: e.target.value })}
              />
              <Input label="Prochaine action" value={follow.next_action} error={fieldError(error, "next_action")} onChange={(e) => setFollow({ ...follow, next_action: e.target.value })} />
              <Input label="À faire le" type="date" value={follow.next_action_on} onChange={(e) => setFollow({ ...follow, next_action_on: e.target.value })} />
              <div className="form-actions">
                <Button type="submit" small busy={busy}>
                  Enregistrer le suivi
                </Button>
              </div>
            </form>
          ) : (
            <KeyValues items={[["Étape", d.follow.stage_label], ["Prochaine action", d.follow.next_action || "—"], ["À faire le", date(d.follow.next_action_on)]]} />
          )}
          <p className="small muted">Le suivi organise votre travail ; il ne modifie pas le statut du dossier ni les contrôles.</p>
        </Card>
        <Card title="Visites">
          {d.visits.length ? (
            <ul className="list-plain location-timeline">
              {d.visits.map((v) => (
                <li key={v.id}>
                  {v.label} (heure de Paris)
                  <span className="block muted small">
                    {v.status === "confirmee" ? (v.past ? (v.attendance === "venu" ? "Venu" : v.attendance === "absent" ? "Absent" : "Présence non renseignée") : "Confirmée") : `Annulée par ${v.cancelled_by === "candidat" ? "le candidat" : "le bailleur"}`}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucune visite réservée.</p>
          )}
        </Card>
        <Card title="Logement">
          <KeyValues
            items={[
              ["Logement", d.campaign.property_label],
              ["Conditions", rentLine(d.campaign.rent_cents, d.campaign.charges_cents)],
              ["Campagne", d.campaign.status_label],
            ]}
          />
        </Card>
        <Card title="Historique">
          <ol className="list-plain location-timeline">
            {d.events.map((e) => (
              <li key={e.id}>
                <strong>{eventText(e)}</strong>
                <span className="block muted small">
                  {dateTime(e.created_at)} · {e.author}
                  {e.note ? ` · « ${e.note} »` : ""}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Messages côté bailleur

type Message = { id: string; author_label: string; body: string; created_at: string; side: "candidat" | "bailleur"; mine: boolean };

export function Thread({ items, empty }: { items: Message[]; empty: string }) {
  if (!items.length) return <p className="muted">{empty}</p>;
  return (
    <ol className="list-plain location-thread">
      {items.map((m) => (
        <li key={m.id} className={m.mine ? "location-msg location-msg-moi" : "location-msg"}>
          <p className="small">
            <strong>{m.author_label}</strong> · {dateTime(m.created_at)}
          </p>
          <p className="location-msg-body">{m.body}</p>
        </li>
      ))}
    </ol>
  );
}

function LandlordMessages({ applicationId }: { applicationId: string }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<{ items: Message[]; recipients: { name: string; role_label: string }[]; application: { household_label: string; campaign_title: string; status: string } }>(org.api(`/applications/${applicationId}/messages`));
  const templates = useGet<TemplatesData>(org.api("/reply-templates"));
  const [body, setBody] = useState("");
  const [unknown, setUnknown] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const all = [...(templates.data?.items ?? []), ...(templates.data?.suggestions ?? [])];

  async function applyTemplate(i: number) {
    try {
      const r = await post<{ body: string; unknown_variables: string[] }>(org.api(`/applications/${applicationId}/messages/render`), { body: all[i].body });
      setBody(r.body);
      setUnknown(r.unknown_variables);
    } catch (err) {
      setError(err);
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post(org.api(`/applications/${applicationId}/messages`), { body });
      toast("Message envoyé. Le candidat est prévenu par e-mail, sans le contenu du message.");
      setBody("");
      setUnknown([]);
      invalidate(org.api(`/applications/${applicationId}`));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  return (
    <div className="grid-2 location-top">
      <Card title="Conversation">
        <Thread items={q.data.items} empty="Aucun message pour cette candidature." />
      </Card>
      {org.can("operate") ? (
        <Card title="Écrire au candidat">
          <form className="stack" onSubmit={send}>
            <Notice>
              <strong>Destinataire{q.data.recipients.length > 1 ? "s" : ""} :</strong>{" "}
              {q.data.recipients.length ? q.data.recipients.map((r) => `${r.name} (${r.role_label.toLowerCase()})`).join(", ") : "aucune adresse connue"}
              <span className="block small">Candidature « {q.data.application.household_label} » pour « {q.data.application.campaign_title} ». Chaque personne reçoit un avis séparé ; le message se lit dans l’espace candidat.</span>
            </Notice>
            <ErrorBox error={error} />
            {all.length ? (
              <Select
                label="Partir d’une réponse type"
                value=""
                options={[{ value: "", label: "Choisir…" }, ...all.map((t, i) => ({ value: String(i), label: t.title }))]}
                onChange={(e) => e.target.value !== "" && applyTemplate(Number(e.target.value))}
              />
            ) : null}
            {unknown.length ? <Notice tone="attention">Variable non remplacée : {unknown.join(", ")}. Complétez le texte avant l’envoi.</Notice> : null}
            <Textarea label="Message" rows={9} required value={body} error={fieldError(error, "body")} onChange={(e) => setBody(e.target.value)} help="Restez factuel : une demande de précision n’est pas une accusation. Ne joignez pas de pièce sensible." />
            <div className="form-actions">
              <Button type="submit" busy={busy} disabled={!body.trim()}>
                Envoyer
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}
